import React, { useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useApplicationStore } from 'hooks/application'
import api from 'helpers/api'
import Menu, { MenuProps } from 'components/menu'
import { Copy, Delete, Plus, Text, Link, PageBreak } from 'components/icons'
import { useDataContext } from 'components/document/contexts/data'
import useProject from 'hooks/useProject'
import Toggle from 'components/toggle'
import { IProcessSectionObject } from 'types'
import { addSectionToProcess, findSectionParent } from 'components/document/helpers/functions'
import { INITIAL_CONTEXT_MENU_STATE } from 'app-constants'

interface SettingsMenuProps extends MenuProps {
  section: IProcessSectionObject
  setDeleteSectionModal: (open: boolean) => void
  addSection: (parentId: string | null) => void
  sectionNumber: string
}

const Settings: React.FC<SettingsMenuProps> = ({
  id,
  menuState,
  setMenuState,
  width,
  section,
  setDeleteSectionModal,
  addSection,
  sectionNumber
}) => {
  const { process, updateProcess, isAdmin } = useDataContext()
  const { project } = useProject()
  const [duplicatingProcessSection, setDuplicatingProcessSection] = useState<{
    isDuplicating: boolean
    status: string | null
  }>({
    isDuplicating: false,
    status: null
  })
  const { displayErrorMessage, setSnackbarMessage } = useApplicationStore()
  const [searchParams] = useSearchParams()

  const updateProcessSection = (
    sectionId: string,
    children: IProcessSectionObject[],
    sectionUpdated: IProcessSectionObject
  ): IProcessSectionObject[] => {
    children.forEach((child, index) => {
      if (child.publicId === sectionUpdated?.publicId) {
        children[index] = sectionUpdated
        return children
      } else {
        return updateProcessSection(sectionId, child.children, sectionUpdated)
      }
    })
    return children
  }

  const updateDescriptionDownload = (inDownload: boolean) => {
    if (process) {
      if (!section.pdfIncludeSection) {
        setSnackbarMessage({
          status: 'error',
          message: 'Include this section in the download first, then you can include its description.'
        })
        return
      }

      api
        .updateProcessSection({
          name: section.name,
          description: section.description,
          processId: process.publicId,
          processSectionId: section.publicId,
          pdfIncludeDescription: section.pdfIncludeSection ? inDownload : false,
          pdfIncludeSection: section.pdfIncludeSection,
          pageBreakBefore: section.pageBreakBefore,
          context: { projectId: project.publicId }
        })
        .then((response) => {
          updateProcess({
            ...process,
            children: updateProcessSection(section.publicId, process.children, response.data)
          })
        })
        .catch((error) => {
          displayErrorMessage(error)
        })
    }
  }

  const updateSectionDownload = (inDownload: boolean) => {
    if (process) {
      api
        .updateProcessSection({
          name: section.name,
          description: section.description,
          processId: process.publicId,
          processSectionId: section.publicId,
          pdfIncludeDescription: inDownload,
          pdfIncludeSection: inDownload,
          pageBreakBefore: section.pageBreakBefore,
          context: { projectId: project.publicId }
        })
        .then((response) => {
          updateProcess({
            ...process,
            children: updateProcessSection(section.publicId, process.children, response.data)
          })
        })
        .catch((error) => {
          displayErrorMessage(error)
        })
    }
  }

  const updatePageBreakBefore = (pageBreakBefore: boolean) => {
    if (process) {
      api
        .updateProcessSection({
          name: section.name,
          description: section.description,
          processId: process.publicId,
          processSectionId: section.publicId,
          pdfIncludeDescription: section.pdfIncludeDescription,
          pdfIncludeSection: section.pdfIncludeSection,
          pageBreakBefore,
          context: { projectId: project.publicId }
        })
        .then((response) => {
          updateProcess({
            ...process,
            children: updateProcessSection(section.publicId, process.children, response.data)
          })
        })
        .catch((error) => {
          displayErrorMessage(error)
        })
    }
  }

  const duplicateSection = (): void => {
    if (process && section) {
      if (!duplicatingProcessSection.isDuplicating) {
        setDuplicatingProcessSection({ isDuplicating: true, status: null })
        api
          .duplicateProcessSection(project.publicId, process.publicId, section.publicId)
          .then((response) => {
            let parentId = findSectionParent(section.publicId, process)
            if (parentId === process.publicId) parentId = null
            updateProcess({ ...process, children: addSectionToProcess(parentId, process.children, response.data) })
            setDuplicatingProcessSection({ isDuplicating: false, status: 'success' })
            setSnackbarMessage({ status: 'success' })
          })
          .catch((error) => {
            displayErrorMessage(error)
            setDuplicatingProcessSection({ isDuplicating: false, status: 'error' })
          })
      }
    }
  }

  const copySectionLink = () => {
    try {
      const params = new URLSearchParams(searchParams)
      params.set('section', section.publicId)
      const newUrl = window.location.origin + window.location.pathname + '?' + params.toString()
      navigator.clipboard.writeText(newUrl)
      setSnackbarMessage({
        status: 'success',
        message: `Copied to clipboard`
      })
      setMenuState(INITIAL_CONTEXT_MENU_STATE)
    } catch (error) {
      displayErrorMessage(error)
    }
  }

  return (
    <Menu id={id} menuState={menuState} setMenuState={setMenuState} width={width}>
      <div className="p-0 m-0 list-none">
        <div className="w-full bg-surface-sunken font-bold p-2.5 text-base border-b border-divider truncate">
          {sectionNumber} {section.name}
        </div>

        <div className="border-b border-divider">
          <li>
            <div
              className="relative flex items-center rounded text-body select-none transition-all truncate cursor-pointer hover:bg-surface-sunken h-8 leading-[32px] py-0 px-2.5"
              onClick={() => copySectionLink()}
            >
              <Link />
              <span className="ml-2.5">Copy Link To Section</span>
            </div>
          </li>
          {isAdmin && (
            <div>
              <li>
                <div
                  className="relative flex items-center rounded text-body select-none transition-all truncate cursor-pointer hover:bg-surface-sunken h-8 leading-[32px] py-0 px-2.5"
                  onClick={() => {
                    addSection(section.publicId)
                    setMenuState(INITIAL_CONTEXT_MENU_STATE)
                  }}
                  data-cy="download-word"
                >
                  <Plus />
                  <span className="ml-2.5">Add Subsection</span>
                </div>
              </li>
              <li>
                <div
                  className="relative flex items-center rounded text-body select-none transition-all truncate cursor-pointer hover:bg-surface-sunken h-8 leading-[32px] py-0 px-2.5"
                  onClick={() => {
                    duplicateSection()
                    setMenuState(INITIAL_CONTEXT_MENU_STATE)
                  }}
                  data-cy="download-word"
                >
                  <Copy />
                  <span className="ml-2.5">Duplicate Section</span>
                </div>
              </li>
              <li>
                <div
                  className="relative flex items-center rounded text-body select-none transition-all truncate cursor-pointer hover:bg-surface-sunken h-8 leading-[32px] py-0 px-2.5"
                  onClick={() => {
                    setDeleteSectionModal(true)
                    setMenuState(INITIAL_CONTEXT_MENU_STATE)
                  }}
                >
                  <Delete />
                  <span className="ml-2.5">Delete Section</span>
                </div>
              </li>
              <div className="w-full bg-surface-sunken font-bold p-2.5 text-base border-divider border-y border-x-0">
                Export Settings
              </div>
              <li>
                <div className="relative flex items-center rounded text-body select-none transition-all truncate cursor-pointer hover:bg-surface-sunken h-8 leading-[32px] py-0 px-2.5">
                  <Text />
                  <span className="ml-2.5">Include Entire Section</span>
                  <Toggle
                    aria-label="Include Entire Section"
                    toggled={section.pdfIncludeSection}
                    onToggle={(toggle) => {
                      updateSectionDownload(toggle)
                    }}
                  />
                </div>
              </li>
              <li>
                <div className="relative flex items-center rounded text-body select-none transition-all truncate cursor-pointer hover:bg-surface-sunken h-8 leading-[32px] py-0 px-2.5">
                  <Text />
                  <span className="ml-2.5">Include Section Description</span>
                  <Toggle
                    aria-label="Include Section Description"
                    toggled={section.pdfIncludeDescription ? true : false}
                    onToggle={(toggle) => {
                      updateDescriptionDownload(toggle)
                    }}
                  />
                </div>
              </li>

              <li>
                <div className="relative flex items-center rounded text-body select-none transition-all truncate cursor-pointer hover:bg-surface-sunken h-8 leading-[32px] py-0 px-2.5">
                  <PageBreak />
                  <span className="ml-2.5">Page Break Before Section</span>
                  <Toggle
                    aria-label="Page Break Before Section"
                    toggled={section.pageBreakBefore ? true : false}
                    onToggle={(toggle) => {
                      updatePageBreakBefore(toggle)
                    }}
                  />
                </div>
              </li>
            </div>
          )}
        </div>
      </div>
    </Menu>
  )
}

export default Settings
